import { router } from 'expo-router';
import { useCallback } from 'react';

import { queryClient } from '@fhs/client';
import { useHrefs } from '@fhs/routing';
import {
  ILoyaltyQuestUi,
  LoyaltyQuestStatus,
  useLoyaltyActivateQuestMutation,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';

import { ChallengeClickEventLabels } from '../analytics';
import { challengesUiQueryKeys, evaluateChallengesQueryKeys } from '../queries/loyalty.queries';

export function useOnPressChallenge() {
  const { defaultServiceModeHref } = useHrefs();
  const { logRBIEvent } = useCRMEventsContext();
  const loyaltyId = useAppSelector(selectors.loyalty.selectLoyaltyUserId);

  const [activateChallenge] = useLoyaltyActivateQuestMutation();

  return useCallback(
    async (challenge: ILoyaltyQuestUi, componentId?: string) => {
      if (challenge.status === LoyaltyQuestStatus.NOT_ENROLLED) {
        await activateChallenge({
          variables: { questId: challenge.id, loyaltyId },
          onCompleted: response => {
            const challengesUiQueryKey = challengesUiQueryKeys.loyaltyUser({ loyaltyId });
            queryClient.setQueryData(
              challengesUiQueryKey,
              (oldChallengesUi?: ILoyaltyQuestUi[]) =>
                oldChallengesUi?.map(oldChallengeUi =>
                  oldChallengeUi.id === challenge.id
                    ? response.loyaltyActivateQuest
                    : oldChallengeUi
                ) ?? []
            );
            queryClient.invalidateQueries({
              queryKey: evaluateChallengesQueryKeys.loyaltyUser({ loyaltyId }),
            });
          },
        });

        logRBIEvent({
          name: CustomEventNames.CLICK_EVENT,
          type: EventTypes.Other,
          attributes: {
            component: ClickEventComponentNames.BUTTON,
            text: ChallengeClickEventLabels.CHALLENGE_START,
            componentId,
            headerText: challenge.name ?? undefined,
          },
        });
        return;
      }

      router.navigate(defaultServiceModeHref);
      logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Other,
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          text: ChallengeClickEventLabels.ORDER_NOW,
          componentId,
          headerText: challenge.name ?? undefined,
        },
      });
    },
    [activateChallenge, loyaltyId, logRBIEvent, defaultServiceModeHref]
  );
}
